import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Shirt, CheckCircle2, AlertCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { toast } from "sonner";
import axios from "axios";
import { useLanguage } from "@/context/LanguageContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LOGO_URL = "/images/ym6gev0b_AE8ECFCA-9BBA-44D9-A534-DED3210EC5A4.png";

const TSHIRT_MOCKUP_URL = "https://images.unsplash.com/photo-1571455786673-9d9d6c194f90?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzN8MHwxfHNlYXJjaHwxfHxwZXJzb24lMjB3ZWFyaW5nJTIwYmxhY2slMjB0LXNoaXJ0JTIwbW9ja3VwfGVufDB8fHx8MTc3Mjk4OTUwN3ww&ixlib=rb-4.1.0&q=85";

const TSHIRT_SIZES = [
  "Youth Small",
  "Youth Medium",
  "Youth Large",
  "Adult Small",
  "Adult Medium",
  "Adult Large",
  "Adult XL",
  "Adult 2XL"
];

export default function TShirtPreorder() {
  const { t } = useLanguage();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  
  // Scroll to top on page load
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  
  const [formData, setFormData] = useState({
    participant_name: "",
    gender: "",
    age: "",
    parent_name: "",
    parent_email: "",
    parent_phone: "",
    tshirt_size: ""
  });

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.tshirt_size) {
      toast.error("Please select a t-shirt size");
      return;
    }

    setIsSubmitting(true);
    try {
      // Get current language from localStorage
      const currentLanguage = localStorage.getItem('burns-elite-language') || 'en';
      
      await axios.post(`${API}/tshirt-preorders`, {
        ...formData,
        age: parseInt(formData.age),
        language: currentLanguage
      });
      setIsSuccess(true);
      toast.success("Pre-order submitted successfully!");
    } catch (error) {
      console.error("Error submitting pre-order:", error);
      toast.error("Failed to submit pre-order. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSuccess) {
    return (
      <div className="min-h-screen bg-burns-black flex items-center justify-center p-4" data-testid="preorder-success">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="text-center max-w-md"
        >
          <CheckCircle2 className="w-20 h-20 text-burns-gold mx-auto mb-6" />
          <h1 className="font-heading text-4xl font-bold text-white mb-4">{t('tshirt.successTitle')}</h1>
          <p className="text-zinc-400 mb-8">
            {t('tshirt.successMessage')}
          </p>
          <Link to="/">
            <Button className="btn-primary px-8 py-4 rounded-sm">
              {t('tshirt.backToHome')}
            </Button>
          </Link>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-burns-black" data-testid="tshirt-preorder-page">
      {/* Header */}
      <header className="nav-glass sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-20 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3" data-testid="back-link">
            <ArrowLeft className="w-5 h-5 text-zinc-400" />
            <img src={LOGO_URL} alt="Burns Elite Baseball" className="h-12 w-auto" />
          </Link>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          {/* Hero Section */}
          <div className="text-center mb-12">
            <Shirt className="w-16 h-16 text-burns-gold mx-auto mb-6" />
            <h1 className="font-heading text-4xl md:text-5xl font-bold uppercase tracking-tight text-white mb-4" data-testid="preorder-title">
              {t('tshirt.title')} <span className="text-burns-red">{t('tshirt.titleAccent')}</span>
            </h1>
            <p className="text-zinc-400 text-lg max-w-xl mx-auto">
              {t('tshirt.subtitle')} - <span className="text-burns-gold font-semibold">{t('tshirt.only')} $40!</span>
            </p>
          </div>

          {/* Price Notice */}
          <Alert className="bg-burns-red/10 border-burns-red mb-4" data-testid="price-notice">
            <AlertCircle className="h-5 w-5 text-burns-red" />
            <AlertDescription className="text-zinc-300">
              <strong className="text-white text-lg">{t('tshirt.priceNotice')} $40</strong>
              <p className="mt-1">{t('tshirt.priceDescription')}</p>
            </AlertDescription>
          </Alert>

          {/* Important Notice */}
          <Alert className="bg-burns-gold/10 border-burns-gold mb-8" data-testid="preorder-notice">
            <AlertCircle className="h-5 w-5 text-burns-gold" />
            <AlertDescription className="text-zinc-300">
              <strong className="text-burns-gold">{t('tshirt.noObligation')}</strong> {t('tshirt.noObligationDesc')}
            </AlertDescription>
          </Alert>

          {/* T-Shirt Preview */}
          <div className="bg-zinc-950 border border-white/10 rounded-sm p-8 mb-8">
            <div className="grid md:grid-cols-2 gap-8 items-center">
              {/* Mockup Image */}
              <div className="relative">
                <div className="relative overflow-hidden rounded-sm">
                  <img 
                    src={TSHIRT_MOCKUP_URL} 
                    alt="Black T-Shirt Mockup" 
                    className="w-full h-auto object-cover"
                    data-testid="tshirt-mockup-image"
                  />
                  {/* Logo overlay positioned on chest area */}
                  <div className="absolute top-[30%] left-1/2 transform -translate-x-1/2 w-28 md:w-36">
                    <img 
                      src={LOGO_URL} 
                      alt="Burns Elite Logo" 
                      className="w-full h-auto drop-shadow-[0_0_10px_rgba(196,30,58,0.5)]"
                    />
                  </div>
                </div>
                <p className="text-center text-zinc-500 text-sm mt-3 italic">*Mockup representation</p>
              </div>
              
              {/* Product Info */}
              <div className="text-center md:text-left">
                <h2 className="font-heading text-3xl font-bold text-white mb-3">{t('tshirt.productName')}</h2>
                <ul className="text-zinc-400 space-y-2 mb-6">
                  <li className="flex items-center gap-2 justify-center md:justify-start">
                    <span className="w-2 h-2 bg-burns-gold rounded-full"></span>
                    {t('tshirt.feature1')}
                  </li>
                  <li className="flex items-center gap-2 justify-center md:justify-start">
                    <span className="w-2 h-2 bg-burns-gold rounded-full"></span>
                    {t('tshirt.feature2')}
                  </li>
                  <li className="flex items-center gap-2 justify-center md:justify-start">
                    <span className="w-2 h-2 bg-burns-gold rounded-full"></span>
                    {t('tshirt.feature3')}
                  </li>
                  <li className="flex items-center gap-2 justify-center md:justify-start">
                    <span className="w-2 h-2 bg-burns-gold rounded-full"></span>
                    {t('tshirt.feature4')}
                  </li>
                </ul>
                <div className="bg-burns-dark inline-block px-6 py-3 rounded-sm border border-burns-red/50">
                  <p className="text-burns-red font-heading text-2xl tracking-wide">$40</p>
                  <p className="text-zinc-400 text-sm">{t('tshirt.perShirt')}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Pre-order Form */}
          <form onSubmit={handleSubmit} className="space-y-8" data-testid="preorder-form">
            {/* Participant Information */}
            <div className="bg-zinc-950 border border-white/10 rounded-sm p-6">
              <h2 className="font-heading text-xl font-bold text-burns-gold mb-4">{t('tshirt.participantInfo')}</h2>
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <Label htmlFor="participant_name" className="text-zinc-400 mb-2 block">{t('tshirt.participantName')} *</Label>
                  <Input
                    id="participant_name"
                    type="text"
                    value={formData.participant_name}
                    onChange={(e) => handleChange("participant_name", e.target.value)}
                    className="form-input bg-transparent border-b border-white/20 rounded-none focus:border-burns-red"
                    required
                    data-testid="participant-name-input"
                  />
                </div>
                <div>
                  <Label htmlFor="gender" className="text-zinc-400 mb-2 block">{t('tshirt.gender')} *</Label>
                  <Select value={formData.gender} onValueChange={(v) => handleChange("gender", v)} data-testid="gender-select">
                    <SelectTrigger className="bg-transparent border-white/20 text-white h-12">
                      <SelectValue placeholder={t('tshirt.selectGender')} />
                    </SelectTrigger>
                    <SelectContent className="bg-zinc-900 border-white/20">
                      <SelectItem value="Male" className="text-white focus:bg-burns-red">{t('tshirt.male')}</SelectItem>
                      <SelectItem value="Female" className="text-white focus:bg-burns-red">{t('tshirt.female')}</SelectItem>
                      <SelectItem value="Other" className="text-white focus:bg-burns-red">{t('tshirt.other')}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="age" className="text-zinc-400 mb-2 block">{t('tshirt.age')} *</Label>
                  <Input
                    id="age"
                    type="number"
                    min="5"
                    max="99"
                    value={formData.age}
                    onChange={(e) => handleChange("age", e.target.value)}
                    className="form-input bg-transparent border-b border-white/20 rounded-none focus:border-burns-red"
                    required
                    data-testid="age-input"
                  />
                </div>
                <div>
                  <Label htmlFor="tshirt_size" className="text-zinc-400 mb-2 block">{t('tshirt.tshirtSize')} *</Label>
                  <Select value={formData.tshirt_size} onValueChange={(v) => handleChange("tshirt_size", v)} data-testid="tshirt-size-select">
                    <SelectTrigger className="bg-transparent border-white/20 text-white h-12">
                      <SelectValue placeholder={t('tshirt.selectSize')} />
                    </SelectTrigger>
                    <SelectContent className="bg-zinc-900 border-white/20">
                      {TSHIRT_SIZES.map(size => (
                        <SelectItem key={size} value={size} className="text-white focus:bg-burns-red">
                          {size}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            {/* Parent/Guardian Information */}
            <div className="bg-zinc-950 border border-white/10 rounded-sm p-6">
              <h2 className="font-heading text-xl font-bold text-burns-gold mb-4">{t('tshirt.parentInfo')}</h2>
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <Label htmlFor="parent_name" className="text-zinc-400 mb-2 block">{t('tshirt.parentName')} *</Label>
                  <Input
                    id="parent_name"
                    type="text"
                    value={formData.parent_name}
                    onChange={(e) => handleChange("parent_name", e.target.value)}
                    className="form-input bg-transparent border-b border-white/20 rounded-none focus:border-burns-red"
                    required
                    data-testid="parent-name-input"
                  />
                </div>
                <div>
                  <Label htmlFor="parent_email" className="text-zinc-400 mb-2 block">{t('contact.email')} *</Label>
                  <Input
                    id="parent_email"
                    type="email"
                    value={formData.parent_email}
                    onChange={(e) => handleChange("parent_email", e.target.value)}
                    className="form-input bg-transparent border-b border-white/20 rounded-none focus:border-burns-red"
                    required
                    data-testid="parent-email-input"
                  />
                </div>
                <div className="md:col-span-2">
                  <Label htmlFor="parent_phone" className="text-zinc-400 mb-2 block">{t('tshirt.phoneNumber')} *</Label>
                  <Input
                    id="parent_phone"
                    type="tel"
                    value={formData.parent_phone}
                    onChange={(e) => handleChange("parent_phone", e.target.value)}
                    className="form-input bg-transparent border-b border-white/20 rounded-none focus:border-burns-red"
                    required
                    data-testid="parent-phone-input"
                  />
                </div>
              </div>
            </div>

            {/* Submit Button */}
            <div className="text-center">
              <Button
                type="submit"
                className="btn-primary text-lg px-12 py-6 rounded-sm"
                disabled={isSubmitting}
                data-testid="submit-preorder-btn"
              >
                {isSubmitting ? t('tshirt.submitting') : t('tshirt.submitPreorder')}
              </Button>
              <p className="text-zinc-500 text-sm mt-4">
                {t('tshirt.preorderNote')}
              </p>
            </div>
          </form>
        </motion.div>
      </main>
    </div>
  );
}
